import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { EventDto } from '@odo/shared-ui';
import { signUpForEvent } from '../../services/events';
import { toApiError } from '../../services/apiErrors';
import { EVENTS_ACTIVE_KEY } from '../../hooks/queryKeys';

interface EventSignUpResult {
    readonly isSignedUp: (eventId: string) => boolean;
    readonly signUp: (eventId: string) => Promise<void>;
    readonly isLoading: boolean;
    readonly error: string | null;
}

// `signedUp` is resolved server-side per spec §7 - this hook no longer reads
// `user_settings` client-side. It looks the flag up on the cached active
// events list (kept fresh by `useActiveEvents`) so every
// `EventBanner` instance - and the countdown/join badge it renders once
// signed up - reflects the same state without a second network round trip.
export function useEventSignUp(): EventSignUpResult {
    const queryClient = useQueryClient();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const isSignedUp = useCallback(
        (eventId: string) => {
            const events = queryClient.getQueryData<EventDto[]>([EVENTS_ACTIVE_KEY]);
            return events?.find((event) => event.id === eventId)?.signedUp ?? false;
        },
        [queryClient]
    );

    const signUp = useCallback(
        async (eventId: string) => {
            setIsLoading(true);
            setError(null);
            try {
                await signUpForEvent(eventId);

                queryClient.setQueryData<EventDto[]>([EVENTS_ACTIVE_KEY], (old) =>
                    old?.map((event) =>
                        event.id === eventId ? { ...event, signedUp: true } : event
                    )
                );
            } catch (err) {
                setError(toApiError(err).message);
            } finally {
                setIsLoading(false);
            }
        },
        [queryClient]
    );

    return { isSignedUp, signUp, isLoading, error };
}
